import { useState, useRef } from 'react'
import { Upload, Loader2 } from 'lucide-react'
import { adminApi } from '@/lib/api'
import { normalizeAccessoryPhotoFile } from '@/lib/normalizeAccessoryPhoto'

interface AccessoryImageUploaderProps {
  accessoryId: number
  currentImageUrl: string
  onUpload: (url: string) => void
  disabled?: boolean
}

export function AccessoryImageUploader({
  accessoryId,
  currentImageUrl,
  onUpload,
  disabled = false,
}: AccessoryImageUploaderProps) {
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      alert('Только изображения (JPG, PNG, WEBP)')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Файл слишком большой (макс. 10MB)')
      return
    }
    setUploading(true)
    try {
      const prepared = await normalizeAccessoryPhotoFile(file)
      const response = await adminApi.uploadAccessoryImage(accessoryId, prepared)
      onUpload(response.data.url)
    } catch {
      alert('Ошибка загрузки. Попробуйте снова.')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-2">
      {currentImageUrl ? (
        <div className="relative rounded-xl overflow-hidden h-32 group">
          <img src={currentImageUrl} alt="" className="w-full h-full object-contain" style={{ background: '#ffffff' }} />
          {!disabled && (
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-xs font-bold disabled:opacity-50"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Заменить'}
              </button>
            </div>
          )}
        </div>
      ) : (
        !disabled && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full h-28 rounded-xl border border-dashed flex flex-col items-center justify-center gap-1 text-xs font-bold"
            style={{ borderColor: 'var(--cyan-border)', color: 'var(--cyan-bright)' }}
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            Загрузить фото
          </button>
        )
      )}
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
    </div>
  )
}
